import { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  MailIcon,
  PhoneIcon,
  IdentificationIcon,
  LocationMarkerIcon,
  OfficeBuildingIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";
import ThirdPartyServices from "../../services/ThirdParty";
import { HollowDotsSpinner } from "react-epic-spinners";
import fire from "../../utils/Toastr";

export default function EmployeeDetails() {
  const { organizationId, customerId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchEmployeeDetails();
  }, [organizationId, customerId]);

  const fetchEmployeeDetails = async () => {
    setLoading(true);
    try {
      // Fetch employee details
      const empResponse = await ThirdPartyServices.getEmployee(
        organizationId,
        customerId,
      );
      if (empResponse.status && empResponse.data) {
        setEmployee(empResponse.data);

        // Fetch organization details
        const orgResponse =
          await ThirdPartyServices.fetchOrganization(organizationId);
        if (orgResponse.status && orgResponse.data?.organizationDetails) {
          setOrganization(orgResponse.data.organizationDetails);
        }
      } else {
        setError("Employee not found");
      }
    } catch (err) {
      setError("Failed to fetch employee details");
      fire("error", "Failed to fetch employee details");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!employee) return;

    try {
      const response = await ThirdPartyServices.sendEmployeeNotification({
        employeeId: employee.id,
        organizationId: employee.organizationId,
        email: employee.emailAddress,
        name: `${employee.firstName} ${employee.surname}`,
      });

      if (response.status) {
        fire("success", "Notification sent successfully");
      } else {
        fire("error", response.message || "Failed to send notification");
      }
    } catch (err) {
      fire("error", "Failed to send notification");
      console.error("Notification error:", err);
    }
  };

  if (loading) {
    return (
      <Layout title="Loading..." current="thirdparty" pageTag="thirdparty">
        <div className="flex justify-center items-center h-64">
          <HollowDotsSpinner color="#4A90E2" />
        </div>
      </Layout>
    );
  }

  if (error || !employee) {
    return (
      <Layout
        title="Employee Not Found"
        current="thirdparty"
        pageTag="thirdparty"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <UserCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Employee Not Found
            </h3>
            <p className="text-gray-500 mb-4">
              {error || "The requested employee could not be found."}
            </p>
            <button
              onClick={() => navigate("/thirdparty/employees")}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Employees
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title={`${employee.firstName} ${employee.surname}`}
      current="thirdparty"
      pageTag="thirdparty"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/thirdparty/employees"
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Employees
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {employee.firstName} {employee.surname}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Employee ID: {employee.id}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleSendNotification}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <MailIcon className="h-4 w-4 mr-2" />
                Send Notification
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Profile */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
              {/* Profile Header */}
              <div className="px-6 py-8 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="h-20 w-20 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 text-2xl font-bold">
                      {employee.firstName?.charAt(0)}
                      {employee.surname?.charAt(0)}
                    </span>
                  </div>
                  <div className="ml-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {employee.title} {employee.firstName} {employee.surname}
                    </h2>
                    <p className="text-gray-500">{employee.emailAddress}</p>
                    <div className="mt-2 flex items-center space-x-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                      <span className="text-sm text-gray-500">
                        Member since{" "}
                        {new Date(employee.dateCreated).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="px-6 py-8">
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        Full Name
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {employee.firstName} {employee.surname}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        Gender
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {employee.gender}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        Date of Birth
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {new Date(employee.dateOfBirth).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        Marital Status
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {employee.maritalStatus}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        State
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {employee.state}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        BVN
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {employee.bvn}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        National ID
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {employee.nationalIdentificationNumber}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="px-6 py-8 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <MailIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Email Address
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {employee.emailAddress}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Phone Number
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {employee.phoneNumber}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <LocationMarkerIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Residential Address
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {employee.residentialAddress}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Organization Info */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Organization
              </h3>
              <div className="flex items-center">
                <OfficeBuildingIcon className="h-10 w-10 text-gray-400 mr-4" />
                <div>
                  <p className="font-medium text-gray-900">
                    {organization?.companyName || "Loading..."}
                  </p>
                  <p className="text-sm text-gray-500">
                    {organization?.applicationName}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    ID: {employee.organizationId}
                  </p>
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Account Information
              </h3>
              <dl className="space-y-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500">
                    Username
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {employee.userName}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">
                    Date Created
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(employee.dateCreated).toLocaleString()}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Verification Status */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Verification Status
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ShieldCheckIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm text-gray-700">BVN Verified</span>
                  </div>
                  <span className="text-xs font-medium text-green-600">
                    Verified
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <IdentificationIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm text-gray-700">ID Verified</span>
                  </div>
                  <span className="text-xs font-medium text-green-600">
                    Verified
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-blue-500 mr-2" />
                    <span className="text-sm text-gray-700">KYC Status</span>
                  </div>
                  <span className="text-xs font-medium text-blue-600">
                    Complete
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
